import React, { useState, useEffect, useMemo } from 'react';
import { 
  collection, 
  addDoc, 
  getDocs, 
  deleteDoc, 
  doc, 
  query, 
  orderBy,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { clearDataCache, getCachedStudents, dataCache } from '../services/dataCacheService';
import { useAcademicYear } from '../context/AcademicYearContext';
import { 
  getStudentTransportForYear, 
  updateStudentTransportForYear, 
  enrollStudentInYear, 
  unenrollStudentFromYear 
} from '../services/academicYearService';
import { Student, TransportLine, AcademicYear } from '../types';
import { 
  Plus, 
  Trash2, 
  Edit2,
  Check, 
  X, 
  Loader2, 
  Truck,
  Bus,
  List,
  Users,
  UserCheck,
  UserX,
  ChevronDown,
  ChevronUp,
  Search,
  School,
  AlertCircle,
  Phone,
  MapPin,
  Calendar,
  Filter,
  ArrowUpDown,
  RotateCcw,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Printer,
  FileDown,
  RefreshCw
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

type SortField = 'name' | 'driverName' | 'phone' | 'escortName' | 'address' | 'active' | 'yearIds' | 'studentsCount';
type SortDirection = 'asc' | 'desc';

interface TransportFormData {
  name: string;
  driverName: string;
  phone: string;
  escortName: string;
  address: string;
  active: boolean;
  yearIds: string[];
  notes: string;
}

const emptyFormData: TransportFormData = {
  name: '',
  driverName: '',
  phone: '',
  escortName: '',
  address: '',
  active: true,
  yearIds: [],
  notes: ''
};

const TransportManagement: React.FC = () => {
  const { selectedYear, currentYearObj, academicYears } = useAcademicYear();

  const [items, setItems] = useState<TransportLine[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<TransportLine | null>(null);
  const [formData, setFormData] = useState<TransportFormData>(emptyFormData);
  const [modalLoading, setModalLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Sorting state
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [showInactive, setShowInactive] = useState<boolean>(false);
  const [yearFilter, setYearFilter] = useState<string>('current'); // 'all', 'current', or specific year ID
  
  const [selectedLineIds, setSelectedLineIds] = useState<string[]>([]);

  // Expanded student views
  const [expandedTransport, setExpandedTransport] = useState<string | null>(null);
  const [expandedUnassigned, setExpandedUnassigned] = useState(false);

  // Per-item loading states for optimistic fast actions
  const [actionLoadingIds, setActionLoadingIds] = useState<Record<string, boolean>>({});
  const [studentActionLoading, setStudentActionLoading] = useState<Record<string, boolean>>({});

  const collectionName = 'transportLines';

  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const fetchData = async (forceRefresh = false) => {
    let safetyTimer: any = setTimeout(() => {
      setLoading(false);
    }, 5000);
    setLoading(true);
    try {
      // 1. Fetch transport lines
      const q = query(collection(db, collectionName), orderBy('name'));
      const snapshot = await getDocs(q);
      const linesData = snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          name: data.name || '',
          driverName: data.driverName || '',
          phone: data.phone || '',
          escortName: data.escortName || '',
          address: data.address || '',
          active: data.active !== false, // default true
          yearIds: Array.isArray(data.yearIds) ? data.yearIds : (data.yearId ? [data.yearId] : []),
          createdAt: data.createdAt || '',
          notes: data.notes || ''
        } as TransportLine;
      });
      setItems(linesData);

      // 2. Fetch students for the currently selected academic year
      const studentsData = await getCachedStudents(forceRefresh, selectedYear, true);
      setStudents(studentsData);
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, collectionName);
    } finally {
      if (safetyTimer) clearTimeout(safetyTimer);
      setLoading(false);
    }
  };

  const toggleSelectAllLines = (filtered: TransportLine[]) => {
    if (selectedLineIds.length === filtered.length && filtered.length > 0) {
      setSelectedLineIds([]);
    } else {
      setSelectedLineIds(filtered.map(item => item.id));
    }
  };

  const toggleSelectOneLine = (id: string) => {
    setSelectedLineIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkLineStatus = async (targetActive: boolean) => {
    if (selectedLineIds.length === 0) return;
    const scrollPos = window.scrollY;
    setLoading(true);
    try {
      const promises = selectedLineIds.map(id => updateDoc(doc(db, collectionName, id), { active: targetActive }));
      await Promise.all(promises);
      setItems(prev => prev.map(item => selectedLineIds.includes(item.id) ? { ...item, active: targetActive } : item));
      invalidateCaches();
      setSelectedLineIds([]);
    } catch (error) {
      console.error('Error in bulk transport status update:', error);
      handleFirestoreError(error, OperationType.UPDATE, collectionName);
    } finally {
      setLoading(false);
      requestAnimationFrame(() => window.scrollTo({ top: scrollPos, behavior: 'instant' }));
    }
  };

  const handleBulkDeleteLines = async () => {
    if (selectedLineIds.length === 0) return;
    if (!window.confirm(`האם אתה בטוח שברצונך למחוק ${selectedLineIds.length} קווי הסעה מסומנים?`)) return;
    setLoading(true);
    try {
      const promises = selectedLineIds.map(id => deleteDoc(doc(db, collectionName, id)));
      await Promise.all(promises);
      setItems(prev => prev.filter(item => !selectedLineIds.includes(item.id)));
      invalidateCaches();
      setSelectedLineIds([]);
    } catch (error) {
      console.error('Error in bulk transport lines delete:', error);
      handleFirestoreError(error, OperationType.DELETE, collectionName);
    } finally {
      setLoading(false);
    }
  };

  const invalidateCaches = () => {
    clearDataCache();
    dataCache.remove('transport_lines');
    dataCache.remove('transportLines_list');
  };

  // Open Modal for Create
  const handleOpenCreateModal = () => {
    setEditingItem(null);
    setFormData({
      ...emptyFormData,
      active: true,
      yearIds: [selectedYear] // Default to current selected academic year
    });
    setFormError('');
    setIsModalOpen(true);
  };

  // Open Modal for Edit
  const handleOpenEditModal = (item: TransportLine) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      driverName: item.driverName || '',
      phone: item.phone || '',
      escortName: item.escortName || '',
      address: item.address || '',
      active: item.active !== false,
      yearIds: item.yearIds && item.yearIds.length > 0 ? item.yearIds : [selectedYear],
      notes: item.notes || ''
    });
    setFormError('');
    setIsModalOpen(true);
  };

  // Submit Add / Edit Form
  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = formData.name.trim();
    if (!name) {
      setFormError('נא להזין שם קו הסעה / מסיע');
      return;
    }

    if (formData.yearIds.length === 0) {
      setFormError('נא לבחור לפחות שנת לימודים אחת לשיוך הקו');
      return;
    }

    setModalLoading(true);
    setFormError('');

    try {
      if (editingItem) {
        // Update existing transport line
        await updateDoc(doc(db, collectionName, editingItem.id), {
          name,
          driverName: formData.driverName.trim(),
          phone: formData.phone.trim(),
          escortName: formData.escortName.trim(),
          address: formData.address.trim(),
          active: formData.active,
          yearIds: formData.yearIds,
          notes: formData.notes.trim(),
          updatedAt: new Date().toISOString()
        });
      } else {
        // Create new transport line
        await addDoc(collection(db, collectionName), {
          name,
          driverName: formData.driverName.trim(),
          phone: formData.phone.trim(),
          escortName: formData.escortName.trim(),
          address: formData.address.trim(),
          active: formData.active,
          yearIds: formData.yearIds,
          notes: formData.notes.trim(),
          createdAt: new Date().toISOString()
        });
      }

      invalidateCaches();
      setIsModalOpen(false);
      await fetchData(true);
    } catch (error) {
      handleFirestoreError(error, editingItem ? OperationType.UPDATE : OperationType.WRITE, collectionName);
      setFormError('אירעה שגיאה בשמירת הנתונים. נא לנסות שוב.');
    } finally {
      setModalLoading(false);
    }
  };

  // Fast In-line Active/Inactive Toggle for Transport Line
  const handleToggleLineActive = async (item: TransportLine) => {
    const newActive = !item.active;
    const scrollPos = window.scrollY;

    setActionLoadingIds(prev => ({ ...prev, [item.id]: true }));

    // Optimistic UI update
    setItems(prev => prev.map(line => line.id === item.id ? { ...line, active: newActive } : line));

    try {
      await updateDoc(doc(db, collectionName, item.id), {
        active: newActive,
        updatedAt: new Date().toISOString()
      });
      invalidateCaches();
    } catch (error) {
      console.error('Error toggling line active status:', error);
      // Revert on failure
      setItems(prev => prev.map(line => line.id === item.id ? { ...line, active: item.active } : line));
    } finally {
      setActionLoadingIds(prev => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      requestAnimationFrame(() => window.scrollTo({ top: scrollPos, behavior: 'instant' }));
    }
  };

  // Quick In-line Year Assignment for Transport Line
  const handleAssignLineToYear = async (item: TransportLine, yearId: string) => {
    const scrollPos = window.scrollY;
    setActionLoadingIds(prev => ({ ...prev, [item.id]: true }));

    let newYearIds: string[] = [];
    const currentYears = item.yearIds || [];

    if (yearId === '__ALL__') {
      newYearIds = academicYears.map(y => y.id);
    } else if (yearId === '__CLEAR__') {
      newYearIds = [];
    } else {
      if (!currentYears.includes(yearId)) {
        newYearIds = [...currentYears, yearId];
      } else {
        newYearIds = currentYears;
      }
    }

    // Optimistic UI update
    setItems(prev => prev.map(l => l.id === item.id ? { ...l, yearIds: newYearIds } : l));

    try {
      await updateDoc(doc(db, collectionName, item.id), {
        yearIds: newYearIds,
        updatedAt: new Date().toISOString()
      });
      invalidateCaches();
    } catch (error) {
      console.error('Error updating line year assignment:', error);
      setItems(prev => prev.map(l => l.id === item.id ? item : l));
    } finally {
      setActionLoadingIds(prev => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      requestAnimationFrame(() => window.scrollTo({ top: scrollPos, behavior: 'instant' }));
    }
  };

  // Quick In-line Remove Year from Transport Line
  const handleRemoveYearFromLine = async (item: TransportLine, yearIdToRemove: string) => {
    const scrollPos = window.scrollY;
    setActionLoadingIds(prev => ({ ...prev, [item.id]: true }));

    const currentYears = item.yearIds || [];
    const newYearIds = currentYears.filter(id => id !== yearIdToRemove);

    // Optimistic UI update
    setItems(prev => prev.map(l => l.id === item.id ? { ...l, yearIds: newYearIds } : l));

    try {
      await updateDoc(doc(db, collectionName, item.id), {
        yearIds: newYearIds,
        updatedAt: new Date().toISOString()
      });
      invalidateCaches();
    } catch (error) {
      console.error('Error removing year from line:', error);
      setItems(prev => prev.map(l => l.id === item.id ? item : l));
    } finally {
      setActionLoadingIds(prev => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      requestAnimationFrame(() => window.scrollTo({ top: scrollPos, behavior: 'instant' }));
    }
  };

  // Bulk Assign All Lines to Selected Academic Year
  const handleBulkAssignAllLinesToYear = async (targetYear: string) => {
    const yearObj = academicYears.find(y => y.id === targetYear) || currentYearObj;
    if (!window.confirm(`האם אתה בטוח שברצונך לשייך את כל קווי ההסעה (${items.length}) לשנת ${yearObj.name}?`)) {
      return;
    }

    setLoading(true);
    try {
      const promises = items.map(item => {
        const currentYears = item.yearIds || [];
        if (!currentYears.includes(targetYear)) {
          const newYears = [...currentYears, targetYear];
          return updateDoc(doc(db, collectionName, item.id), {
            yearIds: newYears,
            updatedAt: new Date().toISOString()
          });
        }
        return Promise.resolve();
      });

      await Promise.all(promises);
      invalidateCaches();
      await fetchData(true);
    } catch (error) {
      console.error('Error bulk assigning lines to year:', error);
      handleFirestoreError(error, OperationType.UPDATE, collectionName);
    } finally {
      setLoading(false);
    }
  };

  // Delete Transport Line
  const handleDeleteItem = async (item: TransportLine) => {
    const assignedCount = students.filter(s => {
      const trans = getStudentTransportForYear(s, selectedYear);
      return trans.transportName === item.name;
    }).length;

    let confirmMsg = `האם אתה בטוח שברצונך למחוק את קו ההסעה "${item.name}"?`;
    if (assignedCount > 0) {
      confirmMsg += `\nשים לב: ישנם ${assignedCount} תלמידים שמשויכים לקו זה לשנת ${currentYearObj.name}. מחיקת הקו לא תמחק את התלמידים, אך מומלץ לשייכם לקו אחר.`;
    }

    if (!window.confirm(confirmMsg)) return;
    
    setActionLoadingIds(prev => ({ ...prev, [item.id]: true }));
    try {
      await deleteDoc(doc(db, collectionName, item.id));
      invalidateCaches();
      await fetchData(true);
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, collectionName);
    } finally {
      setActionLoadingIds(prev => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
    }
  };

  // Toggle active/inactive status of a student for the current year
  const handleToggleStudentActive = async (student: Student) => {
    const currentActive = student.active !== false;
    const newActive = !currentActive;

    const scrollPos = window.scrollY;
    setStudentActionLoading(prev => ({ ...prev, [student.id]: true }));

    // Optimistic UI update
    setStudents(prev => prev.map(s => {
      if (s.id !== student.id) return s;
      return {
        ...s,
        active: newActive,
        enrollments: {
          ...s.enrollments,
          [selectedYear]: {
            ...s.enrollments?.[selectedYear],
            active: newActive
          }
        }
      };
    }));

    try {
      if (newActive) {
        await enrollStudentInYear(student.id, selectedYear, student.classId, student.className);
      } else {
        await unenrollStudentFromYear(student.id, selectedYear);
      }
    } catch (error) {
      console.error('Error toggling student active status:', error);
      setStudents(prev => prev.map(s => s.id === student.id ? student : s));
    } finally {
      setStudentActionLoading(prev => {
        const next = { ...prev };
        delete next[student.id];
        return next;
      });
      requestAnimationFrame(() => window.scrollTo({ top: scrollPos, behavior: 'instant' }));
    }
  };

  // Reassign or unassign a student's transport for this year
  const handleReassignStudentTransport = async (student: Student, targetTransportName: string) => {
    const finalTransport = targetTransportName === '__NONE__' ? '' : targetTransportName;
    const scrollPos = window.scrollY;

    setStudentActionLoading(prev => ({ ...prev, [student.id]: true }));

    // Optimistic UI update
    setStudents(prev => prev.map(s => {
      if (s.id !== student.id) return s;
      return {
        ...s,
        transportName: finalTransport,
        enrollments: {
          ...s.enrollments,
          [selectedYear]: {
            ...s.enrollments?.[selectedYear],
            transportName: finalTransport
          }
        }
      };
    }));

    try {
      await updateStudentTransportForYear(student.id, selectedYear, finalTransport, student.escortName);
    } catch (error) {
      console.error('Error updating student transport:', error);
      setStudents(prev => prev.map(s => s.id === student.id ? student : s));
    } finally {
      setStudentActionLoading(prev => {
        const next = { ...prev };
        delete next[student.id];
        return next;
      });
      requestAnimationFrame(() => window.scrollTo({ top: scrollPos, behavior: 'instant' }));
    }
  };

  // Map students by transport line for the selected academic year
  const transportStudentMap = useMemo(() => {
    const map: Record<string, { active: Student[]; inactive: Student[] }> = {};
    
    // Initialize map for all existing lines
    items.forEach(item => {
      map[item.name] = { active: [], inactive: [] };
    });

    const unassigned: { active: Student[]; inactive: Student[] } = { active: [], inactive: [] };

    students.forEach(student => {
      const transInfo = getStudentTransportForYear(student, selectedYear);
      const lineName = transInfo.transportName?.trim();
      const isActive = student.active !== false;

      if (!lineName) {
        if (isActive) unassigned.active.push(student);
        else unassigned.inactive.push(student);
      } else {
        if (!map[lineName]) {
          map[lineName] = { active: [], inactive: [] };
        }
        if (isActive) map[lineName].active.push(student);
        else map[lineName].inactive.push(student);
      }
    });

    return { map, unassigned };
  }, [items, students, selectedYear]);

  // Handle Sort Toggle
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Helper to format year badges
  const getYearName = (yearId: string) => {
    const found = academicYears.find(y => y.id === yearId);
    return found ? found.name : yearId;
  };

  // Filter & Sort Items
  const processedItems = useMemo(() => {
    let result = [...items];

    // 1. Text Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(item => {
        const matchesField = 
          item.name.toLowerCase().includes(term) ||
          (item.driverName && item.driverName.toLowerCase().includes(term)) ||
          (item.phone && item.phone.includes(term)) ||
          (item.escortName && item.escortName.toLowerCase().includes(term)) ||
          (item.address && item.address.toLowerCase().includes(term)) ||
          (item.notes && item.notes.toLowerCase().includes(term));

        if (matchesField) return true;

        // Check if any student assigned to this line matches the search
        const group = transportStudentMap.map[item.name];
        if (group) {
          return [...group.active, ...group.inactive].some(s => 
            s.fullName.toLowerCase().includes(term) || (s.className && s.className.toLowerCase().includes(term))
          );
        }
        return false;
      });
    }

    // 2. Status filter
    if (statusFilter === 'active') {
      result = result.filter(item => item.active !== false);
    } else if (statusFilter === 'inactive') {
      result = result.filter(item => item.active === false);
    } else {
      // By default (showInactive === false), inactive transport lines are hidden
      if (!showInactive) {
        result = result.filter(item => item.active !== false);
      }
    }

    // 3. Academic Year filter
    if (yearFilter === 'current') {
      // Must be associated with current selectedYear, or have no year restrictions (legacy)
      result = result.filter(item => 
        !item.yearIds || item.yearIds.length === 0 || item.yearIds.includes(selectedYear)
      );
    } else if (yearFilter !== 'all') {
      // Specific year ID selected
      result = result.filter(item => 
        item.yearIds && item.yearIds.includes(yearFilter)
      );
    }

    // 4. Sort
    result.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '', 'he');
          break;
        case 'driverName':
          comparison = (a.driverName || '').localeCompare(b.driverName || '', 'he');
          break;
        case 'phone':
          comparison = (a.phone || '').localeCompare(b.phone || '');
          break;
        case 'escortName':
          comparison = (a.escortName || '').localeCompare(b.escortName || '', 'he');
          break;
        case 'address':
          comparison = (a.address || '').localeCompare(b.address || '', 'he');
          break;
        case 'active': {
          const aActive = a.active !== false ? 1 : 0;
          const bActive = b.active !== false ? 1 : 0;
          comparison = aActive - bActive;
          break;
        }
        case 'yearIds': {
          const aYears = (a.yearIds || []).join(',');
          const bYears = (b.yearIds || []).join(',');
          comparison = aYears.localeCompare(bYears);
          break;
        }
        case 'studentsCount': {
          const aCount = (transportStudentMap.map[a.name]?.active.length || 0);
          const bCount = (transportStudentMap.map[b.name]?.active.length || 0);
          comparison = aCount - bCount;
          break;
        }
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [items, searchTerm, statusFilter, showInactive, yearFilter, sortField, sortDirection, transportStudentMap, selectedYear]);

  const activeLines = useMemo(() => items.filter(i => i.active !== false), [items]);
  const inactiveLines = useMemo(() => items.filter(i => i.active === false), [items]);

  // Active filters count
  const isFiltered = searchTerm.trim() !== '' || statusFilter !== 'all' || yearFilter !== 'current' || showInactive;
  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setYearFilter('current');
    setShowInactive(false);
  };

  const handlePrintList = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('נא לאפשר חלונות קופצים כדי להדפיס את הרשימה');
      return;
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html dir="rtl" lang="he">
        <head>
          <meta charset="utf-8" />
          <title>רשימת מסיעים והסעות - ${currentYearObj.name}</title>
          <style>
            body { font-family: system-ui, -apple-system, sans-serif; margin: 20px; direction: rtl; }
            .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #d97706; padding-bottom: 12px; margin-bottom: 16px; }
            .header h1 { margin: 0; font-size: 22px; color: #78350f; }
            .header p { margin: 4px 0 0 0; color: #64748b; font-size: 13px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 12px; }
            th { background-color: #fef3c7; color: #78350f; padding: 8px 10px; border: 1px solid #fde68a; text-align: right; }
            td { padding: 8px 10px; border: 1px solid #e2e8f0; text-align: right; }
            tr:nth-child(even) { background-color: #fffbeb; }
            .badge { display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: bold; font-size: 10px; }
            .badge-active { background: #dcfce7; color: #15803d; }
            .badge-inactive { background: #fee2e2; color: #b91c1c; }
            @media print {
              body { margin: 10mm; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <h1>רשימת מסיעים וקווי הסעה</h1>
              <p>שנת לימודים: ${currentYearObj.name} • תאריך הפקה: ${new Date().toLocaleDateString('he-IL')} • סה״כ מוצגים: ${processedItems.length}</p>
            </div>
            <img src="/logo.png" style="height: 48px;" alt="לוגו" onerror="this.style.display='none'" />
          </div>
          <table>
            <thead>
              <tr>
                <th style="width: 35px; text-align: center;">#</th>
                <th>שם הקו / המסיע</th>
                <th>שם הנהג</th>
                <th>מספר טלפון</th>
                <th>שם המלווה</th>
                <th>כתובת / אזור</th>
                <th>סטטוס</th>
                <th>שיוך לשנה״ל</th>
                <th>תלמידים משובצים</th>
              </tr>
            </thead>
            <tbody>
              ${processedItems.map((item, idx) => {
                const assignedCount = transportStudentMap.map[item.name]?.active.length || 0;
                const yearsAssigned = (item.yearIds || []).map(yId => academicYears.find(y => y.id === yId)?.name || yId).join(', ') || 'כל השנים';
                return `
                  <tr>
                    <td style="text-align: center;">${idx + 1}</td>
                    <td style="font-weight: bold;">${item.name}</td>
                    <td>${item.driverName || '-'}</td>
                    <td>${item.phone || '-'}</td>
                    <td>${item.escortName || '-'}</td>
                    <td>${item.address || '-'}</td>
                    <td>
                      <span class="badge ${item.active !== false ? 'badge-active' : 'badge-inactive'}">
                        ${item.active !== false ? 'פעיל' : 'לא פעיל'}
                      </span>
                    </td>
                    <td>${yearsAssigned}</td>
                    <td style="font-weight: bold; text-align: center;">${assignedCount}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          <script>
            window.onload = () => {
              setTimeout(() => { window.print(); }, 400);
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  // Render sort icon on headers
  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown size={13} className="text-slate-400 opacity-60 group-hover:opacity-100" />;
    }
    return sortDirection === 'asc' ? (
      <ChevronUp size={15} className="text-amber-600 font-bold" />
    ) : (
      <ChevronDown size={15} className="text-amber-600 font-bold" />
    );
  };

  // Header quick statistics
  const totalActiveLines = items.filter(i => i.active !== false).length;
  const totalTransportedStudents = Object.values(transportStudentMap.map).reduce(
    (acc: number, curr: { active: Student[]; inactive: Student[] }) => acc + curr.active.length, 
    0
  );

  return (
    <div className="max-w-6xl mx-auto space-y-6" dir="rtl">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-4 bg-amber-500 text-white rounded-3xl shadow-lg shadow-amber-200 shrink-0">
            <Truck size={32} />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-3xl font-black text-slate-900">ניהול מסיעים והסעות</h2>
              <span className="text-xs font-black text-amber-900 bg-amber-100 border border-amber-300 px-3 py-1 rounded-xl">
                {currentYearObj.name}
              </span>
            </div>
            <p className="text-slate-500 text-sm mt-0.5">
              פרטי נהגים, מלווים, מסלולים, שיוך לשנות לימודים ורשימות תלמידים
            </p>
          </div>
        </div>

        {/* Action Buttons: Add New Line & Bulk Assign */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => fetchData(true)}
            disabled={loading}
            className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 px-3.5 py-2.5 rounded-2xl font-bold transition-all flex items-center justify-center gap-2 text-xs shrink-0 shadow-sm disabled:opacity-50"
            title="רענן נתונים מהשרת"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            <span>רענן</span>
          </button>

          <button
            onClick={handlePrintList}
            className="bg-white hover:bg-amber-50 text-amber-800 border border-amber-200 px-3.5 py-2.5 rounded-2xl font-bold transition-all flex items-center justify-center gap-2 text-xs shrink-0 shadow-sm"
            title="הדפסת רשימת מסיעים והסעות או שמירה כ-PDF"
          >
            <Printer size={16} />
            <span>הדפסת רשימה</span>
          </button>

          <button
            onClick={() => handleBulkAssignAllLinesToYear(selectedYear)}
            className="bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-300 px-4 py-2.5 rounded-2xl font-bold transition-all flex items-center justify-center gap-2 text-xs shrink-0 shadow-xs"
            title={`שייך את כל ${items.length} הקווים לשנת ${currentYearObj.name}`}
          >
            <Calendar size={16} className="text-amber-700" />
            <span>שייך את כל הקווים לשנת {currentYearObj.name.split(' ')[0]}</span>
          </button>

          <button
            onClick={handleOpenCreateModal}
            className="bg-amber-600 hover:bg-amber-700 text-white px-5 py-2.5 rounded-2xl font-bold shadow-lg shadow-amber-100 transition-all flex items-center justify-center gap-2 text-xs shrink-0"
          >
            <Plus size={18} />
            <span>הוספת מסיע / קו חדש</span>
          </button>
        </div>
      </div>

      {/* Academic Year Info Banner & Quick Filter Tabs (Consistent with Student & Staff Management) */}
      <div className="bg-gradient-to-r from-amber-500 via-amber-600 to-orange-600 rounded-3xl p-6 text-white shadow-lg flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/10 rounded-2xl backdrop-blur-md">
            <Truck size={28} className="text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wider text-amber-200 font-bold">שנת לימודים פעילה</span>
              <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-black">{currentYearObj.name}</span>
            </div>
            <h3 className="text-xl font-bold mt-0.5">ניהול מסיעים והסעות</h3>
            <p className="text-xs text-amber-100 mt-1">
              סה״כ {items.length} קווים במאגר • {activeLines.length} פעילים • {totalTransportedStudents} תלמידים מוסעים {inactiveLines.length > 0 ? `• ${inactiveLines.length} לא פעילים (${showInactive ? 'מוצגים כעת' : 'מוסתרים כברירת מחדל'})` : ''}
            </p>
          </div>
        </div>

        {/* Status Filter Tabs */}
        <div className="flex bg-black/20 p-1.5 rounded-2xl backdrop-blur-md text-xs font-bold shrink-0 self-start md:self-auto gap-1">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-2 rounded-xl transition-all ${
              statusFilter === 'all' 
                ? 'bg-white text-amber-900 shadow-sm' 
                : 'text-white/80 hover:text-white'
            }`}
          >
            כל המאגר ({items.length})
          </button>
          <button
            onClick={() => setStatusFilter('active')}
            className={`px-3 py-2 rounded-xl transition-all ${
              statusFilter === 'active' 
                ? 'bg-white text-amber-900 shadow-sm' 
                : 'text-white/80 hover:text-white'
            }`}
          >
            פעילים ({activeLines.length})
          </button>
          <button
            onClick={() => {
              setStatusFilter('inactive');
              setShowInactive(true);
            }}
            className={`px-3 py-2 rounded-xl transition-all ${
              statusFilter === 'inactive' 
                ? 'bg-red-400 text-red-950 shadow-sm' 
                : 'text-white/80 hover:text-white'
            }`}
          >
            לא פעילים ({inactiveLines.length})
          </button>
        </div>
      </div>

      {/* KPI Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-3">
          <div className="p-3 bg-amber-50 text-amber-600 rounded-xl">
            <Bus size={20} />
          </div>
          <div>
            <div className="text-xl font-black text-slate-900">{items.length}</div>
            <div className="text-xs text-slate-500 font-medium">סה"כ קווי הסעה</div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-3">
          <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
            <CheckCircle2 size={20} />
          </div>
          <div>
            <div className="text-xl font-black text-emerald-700">{totalActiveLines}</div>
            <div className="text-xs text-slate-500 font-medium">קווים פעילים</div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-3">
          <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
            <Users size={20} />
          </div>
          <div>
            <div className="text-xl font-black text-indigo-700">{totalTransportedStudents}</div>
            <div className="text-xs text-slate-500 font-medium">תלמידים מוסעים</div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-3">
          <div className="p-3 bg-rose-50 text-rose-600 rounded-xl">
            <AlertCircle size={20} />
          </div>
          <div>
            <div className="text-xl font-black text-rose-700">{transportStudentMap.unassigned.active.length}</div>
            <div className="text-xs text-slate-500 font-medium">ללא שיוך להסעה</div>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-slate-700 font-bold text-sm">
            <Filter size={16} className="text-amber-500" />
            <span>סינון וחיפוש מסיעים</span>
          </div>

          {isFiltered && (
            <button
              onClick={resetFilters}
              className="text-xs font-bold text-amber-700 hover:text-amber-900 flex items-center gap-1.5 px-3 py-1 bg-amber-50 hover:bg-amber-100 rounded-xl transition-all"
            >
              <RotateCcw size={12} />
              <span>איפוס מסננים</span>
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
          {/* Free Text Search */}
          <div className="relative md:col-span-6">
            <Search size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="חיפוש לפי שם קו, נהג, טלפון, מלווה, כתובת..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pr-10 pl-4 py-2.5 rounded-2xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-amber-500 outline-none text-xs font-medium transition-all"
            />
          </div>

          {/* Academic Year Filter */}
          <div className="md:col-span-3">
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              className="w-full py-2.5 px-3 rounded-2xl bg-slate-50 border border-slate-200 text-xs font-bold text-slate-700 outline-none focus:ring-2 focus:ring-amber-500 hover:border-amber-300 cursor-pointer"
            >
              <option value="current">שנת הלימודים הפעילה ({currentYearObj.name})</option>
              <option value="all">כל שנות הלימוד (ללא סינון שנה)</option>
              {academicYears.map(y => (
                <option key={y.id} value={y.id}>משויך לשנה: {y.name}</option>
              ))}
            </select>
          </div>

          {/* Checkbox: Show / Hide Inactive Transports (default unchecked / hidden) */}
          <div className="md:col-span-3 flex items-center">
            <label className={`flex items-center gap-2.5 px-3.5 py-2 rounded-2xl border transition-all cursor-pointer select-none w-full ${
              showInactive 
                ? 'bg-red-50 border-red-200 text-red-800 shadow-sm' 
                : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300'
            }`}>
              <input
                type="checkbox"
                id="toggle-inactive-transport"
                checked={showInactive}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setShowInactive(checked);
                  if (!checked && statusFilter === 'inactive') {
                    setStatusFilter('all');
                  }
                }}
                className="w-4 h-4 rounded text-amber-600 focus:ring-amber-500 cursor-pointer accent-amber-600"
              />
              <span className="flex items-center gap-1.5 text-xs font-bold">
                <UserX size={16} className={showInactive ? 'text-red-500' : 'text-slate-400'} />
                <span>הצג מסיעים לא פעילים</span>
                {inactiveLines.length > 0 && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                    showInactive ? 'bg-red-200 text-red-900' : 'bg-slate-200 text-slate-600'
                  }`}>
                    {inactiveLines.length}
                  </span>
                )}
              </span>
            </label>
          </div>
        </div>

        {/* Summary and Inactive Feedback */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs text-slate-500 pt-2 border-t border-slate-100">
          <div className="flex items-center gap-2 flex-wrap">
            <span>
              מוצגים <strong className="text-slate-900 font-bold">{processedItems.length}</strong> מסיעים / קווים
            </span>
            {!showInactive && inactiveLines.length > 0 && statusFilter !== 'inactive' && (
              <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-600 px-2.5 py-1 rounded-xl text-[11px] font-semibold border border-slate-200">
                <UserX size={13} className="text-slate-400" />
                <span>{inactiveLines.length} מסיעים לא פעילים מוסתרים (ברירת מחדל)</span>
                <button 
                  type="button"
                  onClick={() => setShowInactive(true)}
                  className="text-amber-700 hover:text-amber-900 underline font-bold cursor-pointer mr-1"
                  title="סמן ב-V להצגת מסיעים לא פעילים"
                >
                  הצג אותם
                </button>
              </span>
            )}
            {showInactive && inactiveLines.length > 0 && (
              <span className="inline-flex items-center gap-1.5 bg-red-50 text-red-700 px-2.5 py-1 rounded-xl text-[11px] font-semibold border border-red-200">
                <UserX size={13} className="text-red-500" />
                <span>כולל {inactiveLines.length} מסיעים לא פעילים</span>
                <button 
                  type="button"
                  onClick={() => {
                    setShowInactive(false);
                    if (statusFilter === 'inactive') setStatusFilter('all');
                  }}
                  className="text-red-700 hover:text-red-900 underline font-bold cursor-pointer mr-1"
                  title="בטל סימון V להסתרת מסיעים לא פעילים"
                >
                  הסתר
                </button>
              </span>
            )}
          </div>
          {isFiltered && (
            <button
              onClick={resetFilters}
              className="text-xs font-bold text-amber-700 hover:text-amber-900 flex items-center gap-1.5 px-3 py-1 bg-amber-50 hover:bg-amber-100 rounded-xl transition-all"
            >
              <RotateCcw size={12} />
              <span>איפוס מסננים</span>
            </button>
          )}
        </div>
      </div>

      {/* Unassigned Students Alert Banner */}
      {transportStudentMap.unassigned.active.length > 0 && (
        <div className="bg-amber-50/90 border border-amber-200 rounded-3xl p-5 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-500 text-white rounded-2xl shrink-0">
                <AlertCircle size={22} />
              </div>
              <div>
                <h4 className="font-bold text-amber-950 text-sm">
                  תלמידים ללא שיוך להסעה לשנת {currentYearObj.name}
                </h4>
                <p className="text-xs text-amber-800 mt-0.5">
                  ישנם <strong className="font-black text-amber-900">{transportStudentMap.unassigned.active.length}</strong> תלמידים פעילים שעדיין אינם משויכים לקו הסעה או מסיע לשנה זו.
                </p>
              </div>
            </div>
            <button
              onClick={() => setExpandedUnassigned(prev => !prev)}
              className="inline-flex items-center gap-1.5 bg-white text-amber-800 border border-amber-300 px-4 py-2 rounded-xl text-xs font-bold hover:bg-amber-100/60 transition-all shadow-sm shrink-0"
            >
              <span>{expandedUnassigned ? 'הסתר תלמידים' : 'הצג תלמידים לשיוך מהיר'}</span>
              {expandedUnassigned ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            </button>
          </div>

          <AnimatePresence>
            {expandedUnassigned && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 pt-4 border-t border-amber-200/80 space-y-3"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                  {transportStudentMap.unassigned.active.map(s => (
                    <div 
                      key={s.id}
                      className="bg-white p-3 rounded-2xl border border-amber-200 shadow-sm flex items-center justify-between gap-2 text-xs"
                    >
                      <div className="min-w-0">
                        <div className="font-bold text-slate-800 truncate">{s.fullName}</div>
                        <div className="text-[11px] text-slate-500">כיתה {s.className || 'ללא כיתה'}</div>
                      </div>

                      <select
                        onChange={(e) => handleReassignStudentTransport(s, e.target.value)}
                        disabled={studentActionLoading[s.id]}
                        defaultValue=""
                        className="bg-amber-50 border border-amber-300 text-amber-950 font-bold rounded-lg px-2 py-1 outline-none text-[11px] hover:border-amber-500 cursor-pointer"
                      >
                        <option value="" disabled>שייך לקו...</option>
                        {items.filter(l => l.active !== false).map(line => (
                          <option key={line.id} value={line.name}>{line.name}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Main Transport Lines Table / Content */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-24 flex flex-col items-center justify-center text-slate-400">
            <Loader2 className="animate-spin mb-4 text-amber-500" size={36} />
            <p className="font-bold text-slate-600">טוען קווי הסעה ומסיעים...</p>
          </div>
        ) : processedItems.length === 0 ? (
          <div className="p-16 text-center text-slate-400">
            <Bus size={44} className="mx-auto mb-3 opacity-25" />
            <p className="font-bold text-slate-700 text-base">לא נמצאו מסיעים בהתאם לסינון הנבחר</p>
            <p className="text-xs text-slate-400 mt-1">נסה לאפס את המסננים או להוסיף קו הסעה חדש.</p>
            <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
              {!showInactive && inactiveLines.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowInactive(true)}
                  className="px-4 py-2 bg-amber-100 text-amber-900 rounded-xl text-xs font-bold hover:bg-amber-200 transition-all inline-flex items-center gap-1.5"
                >
                  <UserX size={14} />
                  <span>הצג {inactiveLines.length} מסיעים לא פעילים</span>
                </button>
              )}
              {isFiltered && (
                <button
                  onClick={resetFilters}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-xl text-xs font-bold hover:bg-slate-200 transition-all inline-flex items-center gap-1.5"
                >
                  <RotateCcw size={14} />
                  <span>איפוס מסננים</span>
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Floating Bulk Actions Bar */}
            <AnimatePresence>
              {selectedLineIds.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 15 }}
                  className="bg-indigo-900 text-white p-3.5 rounded-2xl shadow-xl flex flex-wrap items-center justify-between gap-3 border border-indigo-700/50 mb-4"
                >
                  <div className="flex items-center gap-2">
                    <span className="bg-indigo-800 text-indigo-200 text-xs px-2.5 py-1 rounded-full font-bold">
                      {selectedLineIds.length} נבחרו
                    </span>
                    <span className="text-xs text-indigo-100 font-medium">פעולות מרוכזות על קווי הסעה מסומנים:</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => handleBulkLineStatus(true)}
                      disabled={loading}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
                      title="הגדר קווים מסומנים כפעילים"
                    >
                      <UserCheck size={15} />
                      <span>הגדר כפעילים</span>
                    </button>
                    <button
                      onClick={() => handleBulkLineStatus(false)}
                      disabled={loading}
                      className="bg-amber-600 hover:bg-amber-500 text-white px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
                      title="הגדר קווים מסומנים כלא פעילים (סימון לא פעיל)"
                    >
                      <UserX size={15} />
                      <span>הגדר כלא פעילים</span>
                    </button>
                    <button
                      onClick={handleBulkDeleteLines}
                      disabled={loading}
                      className="bg-rose-600 hover:bg-rose-500 text-white px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
                      title="מחיקת קווי הסעה מסומנים"
                    >
                      <Trash2 size={15} />
                      <span>מחיקה</span>
                    </button>
                    <button
                      onClick={() => setSelectedLineIds([])}
                      className="bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-xl text-xs font-medium transition-all"
                    >
                      ביטול בחירה
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full text-right border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-100 text-slate-700 font-black select-none">
                    {/* Checkbox column */}
                    <th className="px-3 py-3.5 w-10 text-center">
                      <input 
                        type="checkbox"
                        checked={selectedLineIds.length === processedItems.length && processedItems.length > 0}
                        onChange={() => toggleSelectAllLines(processedItems)}
                        className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
                      />
                    </th>
                    {/* Name */}
                    <th 
                      onClick={() => handleSort('name')}
                      className="px-4 py-3.5 cursor-pointer hover:bg-slate-100/70 transition-colors group"
                    >
                      <div className="flex items-center gap-1.5">
                        <span>שם קו / מסיע</span>
                        {renderSortIcon('name')}
                      </div>
                    </th>

                    {/* Driver Name */}
                    <th 
                      onClick={() => handleSort('driverName')}
                      className="px-4 py-3.5 cursor-pointer hover:bg-slate-100/70 transition-colors group"
                    >
                      <div className="flex items-center gap-1.5">
                        <span>שם הנהג</span>
                        {renderSortIcon('driverName')}
                      </div>
                    </th>

                    {/* Phone */}
                    <th 
                      onClick={() => handleSort('phone')}
                      className="px-4 py-3.5 cursor-pointer hover:bg-slate-100/70 transition-colors group"
                    >
                      <div className="flex items-center gap-1.5">
                        <span>מספר טלפון</span>
                        {renderSortIcon('phone')}
                      </div>
                    </th>

                    {/* Escort Name */}
                    <th 
                      onClick={() => handleSort('escortName')}
                      className="px-4 py-3.5 cursor-pointer hover:bg-slate-100/70 transition-colors group"
                    >
                      <div className="flex items-center gap-1.5">
                        <span>שם המלווה</span>
                        {renderSortIcon('escortName')}
                      </div>
                    </th>

                    {/* Address / Route */}
                    <th 
                      onClick={() => handleSort('address')}
                      className="px-4 py-3.5 cursor-pointer hover:bg-slate-100/70 transition-colors group"
                    >
                      <div className="flex items-center gap-1.5">
                        <span>כתובת / מסלול</span>
                        {renderSortIcon('address')}
                      </div>
                    </th>

                    {/* Year Assignment */}
                    <th 
                      onClick={() => handleSort('yearIds')}
                      className="px-4 py-3.5 cursor-pointer hover:bg-slate-100/70 transition-colors group"
                    >
                      <div className="flex items-center gap-1.5">
                        <span>שיוך לשנהל</span>
                        {renderSortIcon('yearIds')}
                      </div>
                    </th>

                    {/* Students count */}
                    <th 
                      onClick={() => handleSort('studentsCount')}
                      className="px-4 py-3.5 cursor-pointer hover:bg-slate-100/70 transition-colors group"
                    >
                      <div className="flex items-center gap-1.5">
                        <span>תלמידים משויכים</span>
                        {renderSortIcon('studentsCount')}
                      </div>
                    </th>

                    {/* Active Status */}
                    <th 
                      onClick={() => handleSort('active')}
                      className="px-4 py-3.5 cursor-pointer hover:bg-slate-100/70 transition-colors group text-center"
                    >
                      <div className="flex items-center justify-center gap-1.5">
                        <span>סטטוס</span>
                        {renderSortIcon('active')}
                      </div>
                    </th>

                    {/* Actions */}
                    <th className="px-4 py-3.5 text-center">פעולות</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {processedItems.map(item => {
                    const group = transportStudentMap.map[item.name] || { active: [], inactive: [] };
                    const isExpanded = expandedTransport === item.id;
                    const isItemLoading = actionLoadingIds[item.id];
                    const isActive = item.active !== false;

                    return (
                      <React.Fragment key={item.id}>
                        <tr className={`hover:bg-amber-50/30 transition-colors group ${selectedLineIds.includes(item.id) ? 'bg-amber-50/40' : ''}`}>
                          {/* Selection Checkbox */}
                          <td className="px-3 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={selectedLineIds.includes(item.id)}
                              onChange={() => toggleSelectOneLine(item.id)}
                              className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
                            />
                          </td>
                          {/* Line Name */}
                          <td className="px-4 py-3 font-bold text-slate-900">
                            <div className="flex items-center gap-2">
                              <div className={`p-2 rounded-xl shrink-0 ${isActive ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-400'}`}>
                                <Bus size={16} />
                              </div>
                              <span className="text-sm font-black">{item.name}</span>
                            </div>
                          </td>

                          {/* Driver Name */}
                          <td className="px-4 py-3 text-slate-700 font-medium">
                            {item.driverName ? (
                              <span>{item.driverName}</span>
                            ) : (
                              <span className="text-slate-300 italic text-[11px]">לא צוין</span>
                            )}
                          </td>

                          {/* Phone */}
                          <td className="px-4 py-3">
                            {item.phone ? (
                              <a
                                href={`tel:${item.phone}`}
                                className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 font-mono font-bold hover:underline"
                                dir="ltr"
                              >
                                <Phone size={12} />
                                <span>{item.phone}</span>
                              </a>
                            ) : (
                              <span className="text-slate-300 italic text-[11px]">-</span>
                            )}
                          </td>

                          {/* Escort Name */}
                          <td className="px-4 py-3 text-slate-700 font-medium">
                            {item.escortName ? (
                              <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md font-bold">
                                {item.escortName}
                              </span>
                            ) : (
                              <span className="text-slate-300 italic text-[11px]">ללא מלווה</span>
                            )}
                          </td>

                          {/* Address / Route */}
                          <td className="px-4 py-3 text-slate-600 max-w-[200px] truncate" title={item.address}>
                            {item.address ? (
                              <div className="flex items-center gap-1">
                                <MapPin size={12} className="text-slate-400 shrink-0" />
                                <span className="truncate">{item.address}</span>
                              </div>
                            ) : (
                              <span className="text-slate-300 italic text-[11px]">-</span>
                            )}
                          </td>

                          {/* Year Assignment */}
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1.5 min-w-[170px]">
                              {/* Badges of assigned years */}
                              <div className="flex flex-wrap items-center gap-1">
                                {item.yearIds && item.yearIds.length > 0 ? (
                                  item.yearIds.map(yearId => {
                                    const isCurrent = yearId === selectedYear;
                                    return (
                                      <span
                                        key={yearId}
                                        className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-lg border transition-all ${
                                          isCurrent
                                            ? 'bg-amber-100 text-amber-950 border-amber-300 shadow-xs'
                                            : 'bg-indigo-50 text-indigo-700 border-indigo-200'
                                        }`}
                                      >
                                        <Calendar size={11} className={isCurrent ? 'text-amber-700' : 'text-indigo-500'} />
                                        <span>{getYearName(yearId).split(' ')[0]}</span>
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveYearFromLine(item, yearId)}
                                          disabled={isItemLoading}
                                          className="hover:text-red-600 transition-colors p-0.5 rounded-full hover:bg-black/5"
                                          title={`הסר שיוך לשנת ${getYearName(yearId)}`}
                                        >
                                          <X size={10} />
                                        </button>
                                      </span>
                                    );
                                  })
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-lg font-medium">
                                    <Calendar size={11} className="text-slate-400" />
                                    <span>כל השנים (ברירת מחדל)</span>
                                  </span>
                                )}
                              </div>

                              {/* In-table quick assignment dropdown */}
                              <select
                                value=""
                                onChange={(e) => {
                                  if (e.target.value) {
                                    handleAssignLineToYear(item, e.target.value);
                                  }
                                }}
                                disabled={isItemLoading}
                                className="w-full text-xs bg-white border border-slate-300 hover:border-amber-400 focus:border-amber-500 rounded-lg py-1 px-2 text-slate-800 outline-none font-bold transition-all shadow-xs cursor-pointer"
                              >
                                <option value="">שייך לשנה״ל...</option>
                                {academicYears.map(y => {
                                  const isAssigned = item.yearIds?.includes(y.id);
                                  return (
                                    <option key={y.id} value={y.id}>
                                      {y.name} {isAssigned ? '✓ (משויך)' : ''}
                                    </option>
                                  );
                                })}
                                <option value="__ALL__">שייך לכל שנות הלימוד</option>
                                {item.yearIds && item.yearIds.length > 0 && (
                                  <option value="__CLEAR__">נקה שיוכים (אפס לכל השנים)</option>
                                )}
                              </select>
                            </div>
                          </td>

                          {/* Enrolled Students Count */}
                          <td className="px-4 py-3">
                            <button
                              onClick={() => setExpandedTransport(isExpanded ? null : item.id)}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-bold transition-all border ${
                                isExpanded
                                  ? 'bg-amber-100 text-amber-900 border-amber-300 shadow-sm'
                                  : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-amber-50 hover:border-amber-200'
                              }`}
                            >
                              <Users size={13} className="text-amber-600" />
                              <span>{group.active.length} תלמידים</span>
                              {group.inactive.length > 0 && (
                                <span className="text-[10px] text-slate-400 font-normal">
                                  (+{group.inactive.length})
                                </span>
                              )}
                              {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                            </button>
                          </td>

                          {/* Active / Inactive Status */}
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleToggleLineActive(item)}
                              disabled={isItemLoading}
                              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full font-bold text-[11px] transition-all border shadow-sm ${
                                isActive
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                  : 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200'
                              }`}
                              title="לחץ לשינוי סטטוס פעיל/לא פעיל"
                            >
                              {isItemLoading ? (
                                <Loader2 size={11} className="animate-spin text-amber-600" />
                              ) : isActive ? (
                                <UserCheck size={12} className="text-emerald-600" />
                              ) : (
                                <UserX size={12} className="text-slate-400" />
                              )}
                              <span>{isActive ? 'פעיל' : 'לא פעיל'}</span>
                            </button>
                          </td>

                          {/* Actions */}
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => handleOpenEditModal(item)}
                                className="p-1.5 text-slate-400 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-all"
                                title="עריכת פרטי מסיע"
                              >
                                <Edit2 size={15} />
                              </button>
                              <button
                                onClick={() => handleDeleteItem(item)}
                                disabled={isItemLoading}
                                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all disabled:opacity-30"
                                title="מחיקת קו הסעה"
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Expandable Students Roster Row */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={10} className="p-0 border-b border-slate-200">
                              <div className="bg-slate-50/70 p-5 space-y-3">
                                <div className="flex items-center justify-between text-xs font-bold text-slate-700">
                                  <div className="flex items-center gap-2">
                                    <Users size={15} className="text-amber-600" />
                                    <span>רשימת תלמידים משויכים לקו {item.name} לשנת {currentYearObj.name}</span>
                                    <span className="text-[11px] font-normal text-slate-500">
                                      ({group.active.length} פעילים, {group.inactive.length} לא פעילים)
                                    </span>
                                  </div>
                                </div>

                                {group.active.length === 0 && group.inactive.length === 0 ? (
                                  <div className="bg-white p-6 rounded-2xl border border-slate-200 text-center text-slate-400">
                                    <Users size={24} className="mx-auto mb-1 opacity-30" />
                                    <p className="text-xs font-medium">אין תלמידים משויכים לקו זה לשנת {currentYearObj.name}</p>
                                  </div>
                                ) : (
                                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                    <table className="w-full text-right text-xs">
                                      <thead>
                                        <tr className="bg-slate-100/60 border-b border-slate-200 text-slate-500 font-bold">
                                          <th className="px-4 py-2">שם התלמיד</th>
                                          <th className="px-4 py-2">כיתה</th>
                                          <th className="px-4 py-2">מלווה אישי</th>
                                          <th className="px-4 py-2">שיוך לשנה״ל</th>
                                          <th className="px-4 py-2">סטטוס תלמיד לשנה זו</th>
                                          <th className="px-4 py-2">העברה לקו אחר</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-slate-100">
                                        {[...group.active, ...group.inactive].map(s => {
                                          const isStudentActive = s.active !== false;
                                          const isUpdating = studentActionLoading[s.id];

                                          return (
                                            <tr key={s.id} className="hover:bg-slate-50/60 transition-colors">
                                              <td className="px-4 py-2">
                                                <div className="flex items-center gap-2">
                                                  <span className="font-bold text-slate-900">{s.fullName}</span>
                                                  {isUpdating && <Loader2 size={11} className="animate-spin text-amber-500" />}
                                                </div>
                                              </td>
                                              <td className="px-4 py-2 text-slate-600">
                                                {s.className || '-'}
                                              </td>
                                              <td className="px-4 py-2 text-slate-500">
                                                {s.escortName || item.escortName || '-'}
                                              </td>
                                              <td className="px-4 py-2">
                                                <div className="flex flex-wrap items-center gap-1">
                                                  {academicYears.filter(y => s.enrollments?.[y.id] && s.enrollments[y.id].active !== false).map(y => (
                                                    <span 
                                                      key={y.id} 
                                                      className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200"
                                                      title={`משויך לשנת ${y.name}`}
                                                    >
                                                      <Calendar size={9} className="text-indigo-500" />
                                                      <span>{y.name.split(' ')[0]}</span>
                                                    </span>
                                                  ))}
                                                  {!academicYears.some(y => s.enrollments?.[y.id] && s.enrollments[y.id].active !== false) && (
                                                    <span className="text-[10px] text-amber-600 font-medium">לא משויך</span>
                                                  )}
                                                </div>
                                              </td>
                                              <td className="px-4 py-2">
                                                <button
                                                  onClick={() => handleToggleStudentActive(s)}
                                                  disabled={isUpdating}
                                                  className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg font-bold text-[11px] transition-all border ${
                                                    isStudentActive
                                                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                                      : 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200'
                                                  }`}
                                                >
                                                  {isStudentActive ? <UserCheck size={11} /> : <UserX size={11} />}
                                                  <span>{isStudentActive ? 'פעיל' : 'לא פעיל'}</span>
                                                </button>
                                              </td>
                                              <td className="px-4 py-2">
                                                <select
                                                  value={item.name}
                                                  onChange={(e) => handleReassignStudentTransport(s, e.target.value)}
                                                  disabled={isUpdating}
                                                  className="bg-slate-50 border border-slate-200 text-slate-700 rounded-lg px-2 py-1 outline-none text-[11px] font-bold cursor-pointer hover:border-amber-300"
                                                >
                                                  <option value={item.name}>{item.name} (נוכחי)</option>
                                                  <option value="__NONE__">ללא הסעה (ביטול שיוך)</option>
                                                  {items.filter(l => l.name !== item.name && l.active !== false).map(l => (
                                                    <option key={l.id} value={l.name}>{l.name}</option>
                                                  ))}
                                                </select>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards View */}
            <div className="lg:hidden divide-y divide-slate-100">
              {processedItems.map(item => {
                const group = transportStudentMap.map[item.name] || { active: [], inactive: [] };
                const isExpanded = expandedTransport === item.id;
                const isItemLoading = actionLoadingIds[item.id];
                const isActive = item.active !== false;

                return (
                  <div key={item.id} className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2.5">
                        <div className={`p-2.5 rounded-2xl shrink-0 ${isActive ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-400'}`}>
                          <Bus size={20} />
                        </div>
                        <div>
                          <h4 className="text-base font-black text-slate-900">{item.name}</h4>
                          <div className="flex flex-wrap items-center gap-2 mt-0.5">
                            <span className="text-xs text-slate-600 font-medium">
                              נהג: {item.driverName || 'לא צוין'}
                            </span>
                            {item.escortName && (
                              <span className="text-xs text-slate-500">
                                • מלווה: {item.escortName}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Active Status Badge */}
                      <button
                        onClick={() => handleToggleLineActive(item)}
                        disabled={isItemLoading}
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full font-bold text-[11px] border shrink-0 ${
                          isActive
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-slate-100 text-slate-500 border-slate-200'
                        }`}
                      >
                        {isItemLoading ? <Loader2 size={11} className="animate-spin" /> : null}
                        <span>{isActive ? 'פעיל' : 'לא פעיל'}</span>
                      </button>
                    </div>

                    {/* Metadata chips */}
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                      {item.phone && (
                        <a
                          href={`tel:${item.phone}`}
                          className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-xl font-mono font-bold"
                          dir="ltr"
                        >
                          <Phone size={12} />
                          <span>{item.phone}</span>
                        </a>
                      )}
                      {item.address && (
                        <div className="inline-flex items-center gap-1 bg-slate-50 px-2.5 py-1 rounded-xl text-slate-600">
                          <MapPin size={12} className="text-slate-400" />
                          <span>{item.address}</span>
                        </div>
                      )}
                    </div>

                    {/* Academic Year chips & quick assign */}
                    <div className="space-y-1.5 pt-1">
                      <div className="flex items-center justify-between text-[11px] font-bold text-slate-700">
                        <span className="flex items-center gap-1">
                          <Calendar size={12} className="text-amber-600" />
                          <span>שיוך לשנה״ל:</span>
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-1">
                        {item.yearIds && item.yearIds.length > 0 ? (
                          item.yearIds.map(yId => {
                            const isCurrent = yId === selectedYear;
                            return (
                              <span
                                key={yId}
                                className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-lg border ${
                                  isCurrent
                                    ? 'bg-amber-100 text-amber-950 border-amber-300'
                                    : 'bg-indigo-50 text-indigo-700 border-indigo-200'
                                }`}
                              >
                                <span>{getYearName(yId).split(' ')[0]}</span>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveYearFromLine(item, yId)}
                                  className="hover:text-red-600 transition-colors p-0.5"
                                  title={`הסר שיוך לשנת ${getYearName(yId)}`}
                                >
                                  <X size={9} />
                                </button>
                              </span>
                            );
                          })
                        ) : (
                          <span className="text-[10px] text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-md font-medium">
                            כל השנים (ברירת מחדל)
                          </span>
                        )}
                      </div>

                      <select
                        value=""
                        onChange={(e) => {
                          if (e.target.value) {
                            handleAssignLineToYear(item, e.target.value);
                          }
                        }}
                        disabled={isItemLoading}
                        className="w-full text-xs bg-white border border-slate-300 rounded-lg py-1 px-2 text-slate-800 outline-none font-bold"
                      >
                        <option value="">+ שייך לשנה״ל...</option>
                        {academicYears.map(y => {
                          const isAssigned = item.yearIds?.includes(y.id);
                          return (
                            <option key={y.id} value={y.id}>
                              {y.name} {isAssigned ? '✓ (משויך)' : ''}
                            </option>
                          );
                        })}
                        <option value="__ALL__">שייך לכל שנות הלימוד</option>
                        {item.yearIds && item.yearIds.length > 0 && (
                          <option value="__CLEAR__">נקה שיוכים (אפס לכל השנים)</option>
                        )}
                      </select>
                    </div>

                    {/* Bottom actions & expand */}
                    <div className="flex items-center justify-between pt-2 border-t border-slate-50">
                      <button
                        onClick={() => setExpandedTransport(isExpanded ? null : item.id)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all border ${
                          isExpanded
                            ? 'bg-amber-100 text-amber-900 border-amber-300'
                            : 'bg-slate-50 text-slate-700 border-slate-200'
                        }`}
                      >
                        <Users size={14} className="text-amber-600" />
                        <span>{group.active.length} תלמידים</span>
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>

                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleOpenEditModal(item)}
                          className="p-2 text-slate-500 hover:text-amber-700 hover:bg-amber-50 rounded-xl"
                          title="עריכה"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteItem(item)}
                          className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-xl"
                          title="מחיקה"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Mobile Student List Expand */}
                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-slate-100 bg-slate-50 p-3 rounded-2xl space-y-2 text-xs">
                        <div className="font-bold text-slate-700 mb-1">
                          תלמידים משויכים ({group.active.length} פעילים):
                        </div>
                        {[...group.active, ...group.inactive].length === 0 ? (
                          <div className="text-slate-400 text-center py-2">אין תלמידים בקו זה לשנה זו</div>
                        ) : (
                          [...group.active, ...group.inactive].map(s => (
                            <div key={s.id} className="bg-white p-2.5 rounded-xl border border-slate-200 flex items-center justify-between gap-2">
                              <div>
                                <div className="font-bold text-slate-900">{s.fullName}</div>
                                <div className="text-[10px] text-slate-500">כיתה {s.className || '-'}</div>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <button
                                  onClick={() => handleToggleStudentActive(s)}
                                  className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                    s.active !== false ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                                  }`}
                                >
                                  {s.active !== false ? 'פעיל' : 'לא פעיל'}
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Add / Edit Transport Line Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-6 sm:p-8 space-y-6 my-8"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-amber-50 text-amber-600 rounded-2xl">
                    <Bus size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-slate-900">
                      {editingItem ? 'עריכת פרטי מסיע / קו' : 'הוספת קו הסעה / מסיע חדש'}
                    </h3>
                    <p className="text-xs text-slate-500">
                      הזן את פרטי הנהג, המלווה, מסלול האיסוף ושיוך לשנות הלימוד
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-all"
                >
                  <X size={20} />
                </button>
              </div>

              {formError && (
                <div className="bg-red-50 text-red-700 p-3 rounded-2xl text-xs font-bold flex items-center gap-2 border border-red-200">
                  <AlertCircle size={16} className="shrink-0" />
                  <span>{formError}</span>
                </div>
              )}

              <form onSubmit={handleSubmitForm} className="space-y-4">
                {/* Line Name */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    שם קו הסעה / מסיע <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="לדוגמה: קו 3 - מזרח, אבו רמי, הסעת צפון..."
                    className="w-full px-4 py-2.5 rounded-2xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-amber-500 outline-none text-sm font-bold"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Driver Name */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      שם הנהג
                    </label>
                    <input
                      type="text"
                      value={formData.driverName}
                      onChange={(e) => setFormData({ ...formData, driverName: e.target.value })}
                      placeholder="שם מלא של הנהג"
                      className="w-full px-4 py-2.5 rounded-2xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-amber-500 outline-none text-sm"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      מספר טלפון
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="050-0000000"
                      className="w-full px-4 py-2.5 rounded-2xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-amber-500 outline-none text-sm font-mono"
                      dir="ltr"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Escort Name */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      שם המלווה
                    </label>
                    <input
                      type="text"
                      value={formData.escortName}
                      onChange={(e) => setFormData({ ...formData, escortName: e.target.value })}
                      placeholder="שם המלווה בהסעה"
                      className="w-full px-4 py-2.5 rounded-2xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-amber-500 outline-none text-sm"
                    />
                  </div>

                  {/* Status Toggle */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      סטטוס פעילות
                    </label>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, active: !formData.active })}
                      className={`w-full py-2.5 px-4 rounded-2xl font-bold text-xs transition-all border flex items-center justify-center gap-2 ${
                        formData.active
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200 shadow-sm'
                          : 'bg-slate-100 text-slate-500 border-slate-200'
                      }`}
                    >
                      {formData.active ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                      <span>{formData.active ? 'קו פעיל במערכת' : 'קו לא פעיל / מושבת'}</span>
                    </button>
                  </div>
                </div>

                {/* Address / Route */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    כתובת / מסלול איסוף
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="שכונות איסוף, תחנות מרכזיות, יעד..."
                    className="w-full px-4 py-2.5 rounded-2xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-amber-500 outline-none text-sm"
                  />
                </div>

                {/* Academic Years Assignment (Multiple Checkboxes) */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-bold text-slate-700">
                      שיוך לשנות לימודים <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        if (formData.yearIds.length === academicYears.length) {
                          setFormData({ ...formData, yearIds: [selectedYear] });
                        } else {
                          setFormData({ ...formData, yearIds: academicYears.map(y => y.id) });
                        }
                      }}
                      className="text-[11px] text-amber-700 hover:text-amber-900 font-bold"
                    >
                      {formData.yearIds.length === academicYears.length ? 'בחר רק שנה נוכחית' : 'בחר כל השנים'}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 bg-slate-50 p-3 rounded-2xl border border-slate-200">
                    {academicYears.map(year => {
                      const isChecked = formData.yearIds.includes(year.id);
                      return (
                        <label
                          key={year.id}
                          className={`flex items-center gap-2 p-2 rounded-xl text-xs font-bold cursor-pointer transition-all border ${
                            isChecked
                              ? 'bg-amber-100/70 border-amber-300 text-amber-950'
                              : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({ ...formData, yearIds: [...formData.yearIds, year.id] });
                              } else {
                                setFormData({ ...formData, yearIds: formData.yearIds.filter(id => id !== year.id) });
                              }
                            }}
                            className="rounded text-amber-600 focus:ring-amber-500 h-4 w-4"
                          />
                          <span>{year.name}</span>
                          {year.id === selectedYear && (
                            <span className="text-[10px] text-amber-700 bg-amber-200/60 px-1.5 py-0.5 rounded mr-auto">
                              שנה נוכחית
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    הערות נוספות
                  </label>
                  <textarea
                    rows={2}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="הערות מיוחדות, זמני איסוף, מגבלות..."
                    className="w-full px-4 py-2 rounded-2xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-amber-500 outline-none text-xs"
                  />
                </div>

                {/* Submit / Cancel Buttons */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-5 py-2.5 rounded-2xl text-xs font-bold text-slate-600 hover:bg-slate-100 transition-all"
                  >
                    ביטול
                  </button>
                  <button
                    type="submit"
                    disabled={modalLoading}
                    className="px-6 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-2xl text-xs font-bold shadow-lg shadow-amber-100 transition-all flex items-center gap-2 disabled:opacity-50"
                  >
                    {modalLoading && <Loader2 size={15} className="animate-spin" />}
                    <span>{editingItem ? 'שמור שינויים' : 'הוסף מסיע'}</span>
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TransportManagement;
